import React from 'react';
import { Tooltip, chakra, Box } from '@chakra-ui/react';
import { Icon as IconifyIcon } from '@iconify/react';
import { CellProps } from 'react-table';
import { CombinedApiData } from '../interfaces/participantTrackerTypes';

// Requirements for a participant to be considered ready for layout design
// (also consumed by the BSR's Imaging column in Modals/SiteReportModal.tsx)
export const REQUIRED_IMAGES = {
  FAF: 1, // 1 FAF macula centered
  FAFONH: 1, // 1 FAF optic nerve head centered
  OCT: 97,
};

const ImagesStatusCell = ({ row }: CellProps<CombinedApiData>) => {
  const imageCounts = row.original.retinal_image_counts;

  if (!imageCounts || Object.keys(imageCounts).length === 0) {
    return (
      <Tooltip label="No retinal images uploaded" hasArrow placement="top">
          <IconifyIcon icon="mdi:image-off" className="text-gray-200 text-[18px]" />
      </Tooltip>
    );
  }

  const checkEye = (eye: 'OD' | 'OS') => {
    const counts = imageCounts[eye];
    if (!counts) return false;
    // Check if counts meet or exceed requirements
    return (counts['FAF'] ?? 0) >= REQUIRED_IMAGES.FAF &&
            (counts['FAFONH'] ?? 0) >= REQUIRED_IMAGES.FAFONH &&
           (counts['OCT'] ?? 0) >= REQUIRED_IMAGES.OCT;
  };
  
  const isODReady = checkEye('OD');
  const isOSReady = checkEye('OS');
  
  const isReady = isODReady || isOSReady; // Ready if at least one eye has all required images

  const tooltipContent = (
    <Box fontSize="0.7rem">
      <chakra.div fontWeight="bold">OD ({isODReady ? 'Ready' : 'Incomplete'}):</chakra.div>
      <chakra.div pl={2}>FAF: {imageCounts.OD?.FAF ?? 0} / {REQUIRED_IMAGES.FAF}</chakra.div>
      <chakra.div pl={2}>FAFONH: {imageCounts.OD?.FAFONH ?? 0} / {REQUIRED_IMAGES.FAFONH}</chakra.div>
      <chakra.div pl={2}>OCT: {imageCounts.OD?.OCT ?? 0} / {REQUIRED_IMAGES.OCT}</chakra.div>
      <chakra.div fontWeight="bold" mt={2}>OS ({isOSReady ? 'Ready' : 'Incomplete'}):</chakra.div>
      <chakra.div pl={2}>FAF: {imageCounts.OS?.FAF ?? 0} / {REQUIRED_IMAGES.FAF}</chakra.div>
      <chakra.div pl={2}>FAFONH: {imageCounts.OS?.FAFONH ?? 0} / {REQUIRED_IMAGES.FAFONH}</chakra.div>
      <chakra.div pl={2}>OCT: {imageCounts.OS?.OCT ?? 0} / {REQUIRED_IMAGES.OCT}</chakra.div>
    </Box>
  );

  return (
    <Tooltip label={tooltipContent} hasArrow placement="top" whiteSpace="normal">
        <IconifyIcon
          icon={isReady ? 'mdi:image-check' : 'mdi:image-alert'}
          // style={{ color: isReady ? theme.colors.green[400] : theme.colors.orange[400], fontSize: '1.2rem' }}
          className={`${isReady ? 'text-green-500' : 'text-orange-500'} text-[18px]`}
        />
    </Tooltip>
  );
};

ImagesStatusCell.displayName = 'ImagesStatusCell';
export default ImagesStatusCell;